import type { CSSProperties, ReactNode } from 'react'

import { designTokens } from '@/shared/lib/design-tokens'
import { typographyStyle } from '@/shared/lib/catalog'
import { cn } from '@/shared/lib'
import { Button, ContentGroup } from '@/shared/ui'

export type AdvertisingCardVariant = 'desktop' | 'mobile'

type AdvertisingCardIconRenderer = (props: {
  color: string
  width: string
  height: string
  strokeWidth: string
}) => ReactNode

export type AdvertisingCardProps = {
  variant?: AdvertisingCardVariant
  title: string
  description?: ReactNode
  ctaLabel?: string
  onCtaClick?: () => void
  icon?: ReactNode | AdvertisingCardIconRenderer
  className?: string
  style?: CSSProperties
}

const desktopTitleStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  ...typographyStyle(designTokens.typography.account.advertisingTitle),
  color: designTokens.colors.neutral.ink,
}

const desktopBodyStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  ...typographyStyle(designTokens.typography.account.advertisingBody),
  color: designTokens.colors.text.strong,
}

const mobileTitleStyle = {
  ...typographyStyle(designTokens.typography.advertising.title),
  color: designTokens.colors.text.primary,
}

const mobileContainerStyle = {
  width: designTokens.sizes.width.advertisingCardMobile,
  padding: designTokens.spacing.md,
  gap: designTokens.spacing.md,
  borderRadius: designTokens.borders.radius.lg,
  borderWidth: designTokens.borders.widths.thin,
  borderStyle: designTokens.borders.styles.solid,
  borderColor: designTokens.colors.border.white,
  background: designTokens.colors.background.surface,
  boxShadow: designTokens.shadows.mobileAdCard,
}

const mobileIconSize = {
  width: designTokens.sizes.width.advertisingCardIconMobile,
  height: designTokens.sizes.height.advertisingCardIconMobile,
  strokeWidth: designTokens.borders.widths.advertisingCardIconMobile,
  color: designTokens.colors.text.primary,
}

const renderIcon = (
  icon: AdvertisingCardProps['icon'],
  props: { color: string; width: string; height: string; strokeWidth: string }
) => {
  if (!icon) {
    return null
  }
  if (typeof icon === 'function') {
    return icon(props)
  }
  return icon
}

const ChevronIcon = ({ color }: { color: string }) => (
  <svg
    width={designTokens.sizes.width.advertisingCardIconMobile}
    height={designTokens.sizes.height.advertisingCardIconMobile}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M9 19L14.3306 12.7809C14.7158 12.3316 14.7158 11.6684 14.3306 11.2191L9 5"
      stroke={color}
      strokeWidth={designTokens.borders.widths.advertisingCardIconMobile}
      strokeLinecap="round"
    />
  </svg>
)

export function AdvertisingCard({
  variant = 'desktop',
  title,
  description,
  ctaLabel,
  onCtaClick,
  icon,
  className,
  style: styleOverride,
}: AdvertisingCardProps) {
  if (variant === 'mobile') {
    return (
      <div
        className={cn('flex flex-col items-start justify-center', className)}
        style={{ ...mobileContainerStyle, ...styleOverride }}
      >
        <div className="flex w-full items-center justify-between">
          {icon ? (
            <div
              className="flex items-center justify-center"
              style={{ width: mobileIconSize.width, height: mobileIconSize.height }}
            >
              {renderIcon(icon, mobileIconSize)}
            </div>
          ) : null}
          <ChevronIcon color={designTokens.colors.text.additional} />
        </div>
        <p className="w-full whitespace-pre-wrap" style={mobileTitleStyle}>
          {title}
        </p>
      </div>
    )
  }

  return (
    <ContentGroup
      variant="transparentPanel"
      gap="16"
      justify="start"
      className={cn('flex flex-1 flex-col items-start self-stretch', className)}
      style={styleOverride}
    >
      <div className="flex w-full flex-col" style={{ gap: designTokens.spacing.lg }}>
        <p style={desktopTitleStyle}>{title}</p>
        {description ? <div style={desktopBodyStyle}>{description}</div> : null}
      </div>
      {ctaLabel ? (
        <Button
          type="button"
          variant="secondarySubmitMobile"
          className="w-full"
          style={{ minHeight: designTokens.components.account.hero.buttonHeight }}
          onClick={onCtaClick}
        >
          {ctaLabel}
        </Button>
      ) : null}
    </ContentGroup>
  )
}
